import { ChangeDetectorRef, Component, ElementRef, EventEmitter, Input, OnChanges, OnInit, Output, QueryList, SimpleChanges, ViewChild, ViewChildren } from '@angular/core';
import { FormBuilder, FormControl, Validators } from '@angular/forms';
import { BaseService } from '@services/base-service.service';
import { API } from '@config/constants/api.constant';
import { BehaviorSubject, Observable } from 'rxjs';
import { ActivatedRoute } from '@angular/router';
import { IListProductScanResponse } from '../../../pages/sales-order/detail-sales-order/scan-products/scanproducts.interface';
import { CustomsSnackbarComponent } from '@shared/components/customs-snackbar/customs-snackbar.component';
import { MatSnackBar } from '@angular/material/snack-bar';
import { STRING_CONSTANTS } from '@config/constants/string.constants';
import { BaseResponse } from '@shared/base/base-response';
import { IResetScanPayload } from '@shared/components/scanner-ui-input/scanner-interface';
import { ModalComponent } from '@shared/components/modal/modal.component';
import { ModalConfig } from '@shared/components/modal/modal.interface';
import { UtilitiesService } from '@services/utilities.service';

export interface IBodyInjectQr {
  qr_code: string;
  sales_order_id: string;
  product_id: string;
}

@Component({
  selector: 'app-scanner-ui-input',
  templateUrl: './scanner-ui-input.component.html',
  styleUrls: ['./scanner-ui-input.component.scss'],
})
export class ScannerUiInputComponent implements OnInit, OnChanges {
  @Input() productId: string;
  @Input() soId: string;
  @Input() isManualInput: boolean = false;
  @Input() resetScanOne: boolean = false;
  @Output() afterFinishScan = new EventEmitter<IListProductScanResponse[] | any>();
  @ViewChildren('inputQrForm') inputQrForm: QueryList<ElementRef>;
  @ViewChildren('inputQROneForm') inputQROneForm: QueryList<ElementRef>;

  @ViewChild('modalErrorScan') modalErrorScan: ModalComponent;
  modalConfigErrorScan: ModalConfig = {
    hideCloseButton: () => true,
    dismissButtonLabel: 'Oke',
  };

  isLoading: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  audioError: any;
  audioSuccess: any;

  STRING_CONSTANTS = STRING_CONSTANTS;
  responseMessage = new BehaviorSubject<{ message: string; soID: string }>({ message: '', soID: '' });

  set input(element: ElementRef<HTMLInputElement>) {
    if (element) {
      element.nativeElement.focus();
    }
  }

  constructor(
    private baseService: BaseService,
    private formBuilder: FormBuilder,
    private ref: ChangeDetectorRef,
    private activeRoute: ActivatedRoute,
    private snackBar: MatSnackBar,
    private utilitiesService: UtilitiesService
  ) {}

  ngOnInit(): void {
    this.checkId();
    this.getSound();
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes) {
      this.soId = changes.soId && !changes.soId.firstChange ? changes.soId.currentValue : this.soId;

      this.productId = changes.productId && !changes.productId.firstChange ? changes.productId.currentValue : this.productId;
    }
  }

  checkId() {
    this.activeRoute.paramMap.subscribe((data) => {
      if (data) {
        this.productId = data.get('product') ?? '';
        this.soId = data.get('so') ?? '';
      }
    });
  }

  scannerInput = this.formBuilder.group({
    qrManual: new FormControl('', [Validators.maxLength(9), Validators.minLength(9)]),
    qrCode: new FormControl('', [Validators.maxLength(9), Validators.minLength(9)]),
    qrCodeOne: new FormControl('', [Validators.maxLength(9), Validators.minLength(9)]),
  });

  onChange($event: string) {
    if ($event.length === 9 && !this.isManualInput) {
      if (this.resetScanOne) {
        this.submit(true)?.then((subs) => this.handleResetScanOne(subs));
      }

      return this.submit();
    }
  }

  private get qrInput() {
    return this.scannerInput.get('qrCode');
  }

  private get qrInputOne() {
    return this.scannerInput.get('qrCodeOne');
  }

  get ManualInput() {
    return this.scannerInput.get('qrManual');
  }

  submit(isResetScanOne = false) {
    let qrInputResult;
    // && (this.scannerInput.controls.qrCode.valid || this.scannerInput.controls.qrCodeOne.valid)
    if (!this.isLoading.value) {
      if (!isResetScanOne) {
        // && this.scannerInput.controls.qrCode.valid
        this.isLoading.next(true);

        qrInputResult = this.qrInput?.value;
        const manualInput = this.ManualInput?.value?.toUpperCase();

        const data: IBodyInjectQr = {
          product_id: this.productId,
          qr_code: qrInputResult || manualInput || '',
          sales_order_id: this.soId,
        };

        this.postData(data);
        this.clearInput();
      }

      if (isResetScanOne) {
        // && this.scannerInput.controls.qrCodeOne.valid
        this.isLoading.next(true);
        qrInputResult = this.isManualInput ? this.ManualInput?.value : this.qrInputOne?.value;
        return this.resetScanData(qrInputResult as string);
      }
    }
  }

  postData(data: IBodyInjectQr) {
    this.baseService.postData<any>(API.INJECT_QR, data, { disableModalPopupError: true }).subscribe((res) => {
      if (res) {
        this.isLoading.next(false);
        this.ref.detectChanges();
        if (!this.isManualInput) {
          this.inputQrForm.first.nativeElement.focus();
        }

        if (res.success) {
          const result = res.data.product_scan_list;
          this.afterFinishScan.emit(result);
          this.SuccessSound();
        } else {
          this.afterFinishScan.emit([]);
          this.handleScanError(res);
        }
      }
    });
  }

  async resetScanData(qrInput: string) {
    const _payload = JSON.stringify({
      qr_code: qrInput,
      sales_order_id: this.soId,
      product_id: this.productId,
    });

    return this.baseService.putData<IResetScanPayload>(API.RESET_SCAN_BY_QRCODE, _payload);
  }

  handleResetScanOne(event$: Observable<BaseResponse<IResetScanPayload>>) {
    event$.subscribe((resp) => {
      if (resp) {
        if (resp.success) {
          const { message } = resp;
          this.isLoading.next(false);
          this.ref.detectChanges();

          if (!this.isManualInput) {
            this.inputQROneForm.first?.nativeElement.focus();
          }

          this.afterFinishScan.emit(message);
          this.clearInput();
        }
      }
    });
  }

  clearInput() {
    this.ManualInput?.setValue('');

    if (this.resetScanOne) {
      this.scannerInput.controls.qrCodeOne.setValue('');
      this.qrInputOne?.setValue('');

      if (!this.isManualInput) {
        this.inputQROneForm?.first?.nativeElement.focus();
      }
    } else {
      this.scannerInput.controls.qrCode.setValue('');
      this.qrInput?.setValue('');
    }

    if (!this.isManualInput && !this.resetScanOne) {
      this.inputQrForm.first.nativeElement.focus();
    }
  }

  getSound() {
    this.audioError = new Audio('https://s3-ap-southeast-1.amazonaws.com/maxxi-staging/file/FILE_22b3a6a7-c203-4a20-92a2-ab1a24840665');
    this.audioSuccess = new Audio('https://s3-ap-southeast-1.amazonaws.com/maxxi-staging/file/FILE_eb5b5414-ca11-4e30-b39f-5cb4330020c6');
    this.audioSuccess.load();
  }

  soundError(showSnackBar = true) {
    // let audio = new Audio('assets/media/sound/error_fix.mp3');
    this.audioError.load();
    this.audioError.play();
    if (showSnackBar) {
      this.showErrorSnackBar();
    }
  }

  SuccessSound() {
    this.audioSuccess.load();
    this.audioSuccess.play();
    this.snackBar.openFromComponent(CustomsSnackbarComponent, {
      duration: 3000,
      horizontalPosition: 'end',
      data: {
        color: '#353535',
        text: 'Scan QR berhasil disimpan',
        icon: STRING_CONSTANTS.ICON.IC_BULLET_TICK_WHITE,
      },
    });
  }

  handleScanError(resp: BaseResponse<any>) {
    if (!resp.errors) {
      return;
    }
    let { message, code, title } = resp.errors[0];

    if (code === 4022) {
      let _title = title.split(' ').slice(0, -1);
      title = this.utilitiesService.arrayStringJoin(_title, '', ' ');
      this.responseMessage.next({
        message: `${title} ${message}`,
        soID: message,
      });
      this.soundError(false);
      return this.modalErrorScan.open();
    } else {
      this.responseMessage.next({
        message: title,
        soID: '',
      });
      this.soundError();
    }
  }

  showErrorSnackBar() {
    this.snackBar.openFromComponent(CustomsSnackbarComponent, {
      duration: 3000,
      horizontalPosition: 'end',
      data: {
        color: '#CC4435',
        text: `${this.responseMessage.value.message}`,
        icon: STRING_CONSTANTS.ICON.IC_FAIL_SCAN_PRODUCT,
      },
    });
  }

  copyToClipboard(data: string) {
    navigator.clipboard.writeText(data).then();
  }
}
